<!-- Toote muutmise vaheleht -->
<template>
  <page-header class="px-10">
    <h1>Muuda toodet</h1>
  </page-header>
  <v-sheet class="post-body d-flex justify-center pa-10 ga-10">
    <div class="products">
      <v-form ref="form">
        <v-file-input
            v-model="selectedImage"
            label="Sisesta pildid"
            variant="outlined"
            prepend-icon="mdi-camera"
            accept="image/*"
            multiple
            :loading="uploadingImage"
            @update:modelValue="uploadImage"
        ></v-file-input>
        <p v-if="uploadError" class="error-text">{{ uploadError }}</p>
        <v-btn
            class="mb-4"
            color="eraklient"
            variant="outlined"
            :loading="loadingCloudinaryImages"
            @click="fetchCloudinaryImages"
        >
          Lae pildid
        </v-btn>
        <p v-if="cloudinaryError" class="error-text">{{ cloudinaryError }}</p>
        <div v-if="cloudinaryImages.length" class="cloudinary-gallery">
          <div
              v-for="image in cloudinaryImages"
              :key="image.public_id"
              class="cloudinary-item"
              :class="{ selected: isSelectedImage(image.url) }"
          >
            <button
                type="button"
                class="cloudinary-select"
                @click="selectCloudinaryImage(image)"
            >
              <img :src="image.url" :alt="image.public_id"/>
            </button>
            <v-btn
                class="cloudinary-delete"
                size="x-small"
                icon="mdi-delete"
                color="error"
                :loading="deletingPublicId === image.public_id"
                @click.stop="openDeleteCloudinaryDialog(image)"
            ></v-btn>
          </div>
        </div>
        <v-dialog v-model="deleteImageDialog" width="auto" transition="none">
          <v-card min-width="360">
            <v-toolbar color="white" title="Kinnitus">
              <v-btn icon="mdi-close" @click="closeDeleteCloudinaryDialog"></v-btn>
            </v-toolbar>
            <v-card-text>
              <v-alert type="warning" variant="tonal" border="start" prominent>
                Kas oled kindel, et soovid selle pildi kustutada?
              </v-alert>
            </v-card-text>
            <v-divider></v-divider>
            <div class="ma-4">
              <v-btn
                  class="text-none mr-4 font-weight-bold"
                  color="error"
                  min-width="92"
                  rounded
                  :loading="deletingPublicId === pendingDeleteImage?.public_id"
                  @click="confirmDeleteCloudinaryImage"
              >
                Jah
              </v-btn>
              <v-btn class="text-none font-weight-bold" min-width="92" rounded @click="closeDeleteCloudinaryDialog">
                Ei
              </v-btn>
            </div>
          </v-card>
        </v-dialog>
        <div v-if="product.image?.length" class="selected-images">
          <div class="mb-4">
            <p class="font-weight-bold">Valitud pildid:</p>
            <p class="selected-images-hint">Esimene pilt on kaanepilt. Kasuta nooli või nuppu, et muuta järjekorda.</p>
          </div>
          <div class="product-image-manager">
            <div
                v-for="(url, index) in product.image"
                :key="`${url}-${index}`"
                class="product-image-item"
                :class="{ 'product-image-item-cover': index === 0 }"
            >
              <div class="product-image-thumb-wrap">
                <v-img :src="url" class="product-image-thumb" cover></v-img>
                <v-chip v-if="index === 0" class="product-image-badge" color="eraklient" size="x-small" variant="flat">
                  Kaanepilt
                </v-chip>
              </div>
              <div class="product-image-actions">
                <v-btn
                    icon="mdi-arrow-up"
                    size="small"
                    variant="text"
                    :disabled="index === 0"
                    @click="moveProductImage(index, -1)"
                ></v-btn>
                <v-btn
                    icon="mdi-arrow-down"
                    size="small"
                    variant="text"
                    :disabled="index === product.image.length - 1"
                    @click="moveProductImage(index, 1)"
                ></v-btn>
                <v-btn
                    v-if="index !== 0"
                    color="eraklient"
                    variant="outlined"
                    size="small"
                    @click="setProductCoverImage(index)"
                >
                  Tee kaanepildiks
                </v-btn>
                <v-btn color="error" variant="text" size="small" @click="removeProductImage(index)">
                  Eemalda
                </v-btn>
              </div>
            </div>
          </div>
          <v-btn color="error" variant="outlined" size="small" @click="setProductImages([])" class="mb-4">
            Eemalda kõik pildid
          </v-btn>
        </div>
        <v-text-field
            v-if="!product.bottle_sizes || product.bottle_sizes.length === 0"
            v-model.number="product.price"
            label="Hind"
            variant="outlined"
            :rules="rules"
        ></v-text-field>
        <v-text-field
            v-if="!product.bottle_sizes || product.bottle_sizes.length === 0"
            v-model.number="product.new_price"
            variant="outlined"
          :label="getSecondaryPriceLabel()"
        ></v-text-field>
        <div class="category-inputs">
          <div v-for="(bs, idx) in product.bottle_sizes" :key="`bs-${idx}`" class="d-flex align-center mb-2">
            <v-text-field
              v-model="product.bottle_sizes[idx].size"
              :label="`Suurus ${idx + 1}`"
              variant="outlined"
              style="flex:1"
            ></v-text-field>
            <v-text-field
              v-model.number="product.bottle_sizes[idx].price"
              :label="`Hind ${idx + 1}`"
              variant="outlined"
              suffix="€"
              class="ml-2"
              style="flex:1"
            ></v-text-field>
            <v-checkbox
              :model-value="!product.bottle_sizes[idx].disabled"
              @update:model-value="val => product.bottle_sizes[idx].disabled = !val"
              label="Aktiivne"
              density="compact"
              class="ml-2"
              hide-details
            ></v-checkbox>
            <v-btn icon color="error" variant="text" @click="removeBottleSize(idx)" class="ml-2">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-btn small color="primary" variant="text" @click="addBottleSize" class="mb-4">+ Lisa pudeli suurus valik</v-btn>
        </div>
        <v-switch
          v-model="product.has_spray_roll_on_option"
          :true-value="true"
          :false-value="false"
          color="eraklient"
          inset
          label="Lisa spray ja roll-on valik"
        ></v-switch>
        <div v-if="product.has_spray_roll_on_option" class="d-flex ml-4 mb-4">
          <v-checkbox
            v-model="product.spray_roll_on_options.spray"
            label="Spray saadaval"
            density="compact"
            hide-details
            class="mr-6"
          ></v-checkbox>
          <v-checkbox
            v-model="product.spray_roll_on_options.roll_on"
            label="Roll-on saadaval"
            density="compact"
            hide-details
          ></v-checkbox>
        </div>
        <div class="category-inputs">
          <div v-for="(cat, idx) in product.category_ids" :key="`cat-${idx}`" class="d-flex align-center mb-2">
            <v-select
              :label="`Kategooria ${idx + 1}`"
              v-model="product.category_ids[idx]"
              :items="categories"
              item-title="name"
              item-value="id"
              :rules="[value => validateCategoryUnique(value, idx) || 'Kategooriad peavad olema erinevad.', value => !!value || 'Ei saa olla tühi.']"
              clearable
              menu-icon="mdi-chevron-down"
              variant="outlined"
              style="flex:1"
            ></v-select>
            <v-btn icon color="error" variant="text" @click="removeCategory(idx)" v-if="product.category_ids.length > 1" class="ml-2">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-btn small color="primary" variant="text" @click="addCategory" class="mb-4">+ Lisa kategooria</v-btn>
        </div>
        <v-tabs v-model="lang_tab" bg-color="eraklient">
          <v-tab value="one" @click="changeLocale('et')">EST</v-tab>
          <v-tab value="two" @click="changeLocale('en')">ENG</v-tab>
          <v-tab value="three" @click="changeLocale('ru')">RUS</v-tab>
        </v-tabs>
        <v-window class="py-5" v-model="lang_tab">
          <v-window-item value="one">
            <v-text-field
                v-model=product.header
                label="Toote nimetus"
                variant="outlined"
                :rules="rules"
            ></v-text-field>
            <div class="py-5">
              <tiptap v-model="product.description"/>
            </div>
          </v-window-item>
          <v-window-item value="two">
            <v-text-field
                v-model=product.header_en
                label="Toote nimetus"
                variant="outlined"
                :rules="rules"
            ></v-text-field>
            <div class="py-5">
              <tiptap v-model="product.description_en"/>
            </div>
          </v-window-item>
          <v-window-item value="three">
            <v-text-field
                v-model=product.header_ru
                label="Toote nimetus"
                variant="outlined"
                :rules="rules"
            ></v-text-field>
            <div class="py-5">
              <tiptap v-model="product.description_ru"/>
            </div>
          </v-window-item>
        </v-window>
      </v-form>
      <v-btn color="success" @click="updateProduct" block class="mt-2">Uuenda</v-btn>
      <alert>
        <template #first-btn>Kustuta</template>
        <div class="text-h5 justify-center text-center">Kas oled kindel, et soovid toote kustutada?</div>
        <template #confirmation-btn>
          <v-btn class="text-none mr-4 font-weight-bold" color="eraklient" min-width="92" rounded @click.prevent="deleteProduct">Jah</v-btn>
        </template>
      </alert>
    </div>

    <div class="products">
      <v-tabs v-model="tab" bg-color="eraklient">
        <v-tab value="one">Kõik tooted</v-tab>
        <v-tab value="two">Toote vaade</v-tab>
      </v-tabs>
      <v-window v-model="tab" class="preview-tabs-window">
        <v-window-item class="py-5" value="one">
          <v-card class="prod-item product-img" flat hover>
            <div class="preview-card-image" @mouseenter="setPreviewHover(true)" @mouseleave="setPreviewHover(false)">
              <div v-if="product.image?.length > 1" class="preview-layered-images">
                <v-img
                    :src="product.image[0]"
                    class="preview-carousel-image base-image"
                    :class="{ 'is-inactive': previewHovered }"
                    height="100%"
                    contain
                    eager
                ></v-img>
                <v-img
                    :src="product.image[1]"
                    class="preview-carousel-image hover-image"
                    :class="{ 'is-active': previewHovered }"
                    height="100%"
                    contain
                    eager
                ></v-img>
              </div>
              <v-img
                  v-else-if="product.image?.length"
                  :src="product.image[0]"
                  class="preview-carousel-image"
                  height="100%"
                  contain
              ></v-img>
              <img v-else class="default-logo" src="../../assets/images/lohnakas.webp" alt="Lohnakas logo"/>
            </div>
            <v-card-item class="mt-5 pa-0 text-center preview-card-content">
              <v-card-title class="font-weight-bold preview-card-title"> <span class="head"> {{ getHeader(product) }} </span> </v-card-title>
              <v-card-subtitle>
                <div class="prices">
                  <p class="price" v-if="hasBottleSizePriceRange(product)">{{ getPriceLabel(product, size) }}</p>
                  <template v-else>
                    <p class="new-price pr-2" v-if="product.new_price !== null && product.new_price !== undefined && String(product.new_price).trim() !== ''"> {{ formatPriceForDisplay(product.new_price) }}€</p>
                    <p class="old-price" v-if="product.new_price !== null && product.new_price !== undefined && String(product.new_price).trim() !== ''"> {{ formatPriceForDisplay(product.price) }}€</p>
                    <p v-if="product.new_price === null || product.new_price === undefined || String(product.new_price).trim() === ''"> {{ formatPriceForDisplay(product.price) }}€</p>
                  </template>
                </div>
              </v-card-subtitle>
              <v-card-text>
                <div class="product-category-text" v-if="getCategoryDisplayGroups(product).length">
                  <div class="product-category-group" v-for="(group, groupIndex) in getCategoryDisplayGroups(product)" :key="`edit-preview-group-${groupIndex}`">
                    <template v-for="(part, index) in group.pathParts" :key="`edit-preview-cat-${groupIndex}-${index}`">
                      <span class="product-category-level" :class="index === 0 ? 'product-category-main' : 'product-category-sub'">{{ part }}</span>
                      <span v-if="index < group.pathParts.length - 1" class="product-category-arrow">›</span>
                    </template>
                    <template v-for="(leaf, leafIndex) in group.leafLabels" :key="`edit-preview-leaf-${groupIndex}-${leafIndex}`">
                      <span class="product-category-level product-category-sub product-category-extra">{{ leaf }}</span>
                    </template>
                  </div>
                </div>
              </v-card-text>
            </v-card-item>
          </v-card>
        </v-window-item>

        <v-window-item class="preview-detail-tab" value="two">
          <v-sheet class="d-flex content-body">
            <v-sheet class="view-preview-media">
              <v-carousel
                  v-if="product.image?.length"
                  v-model="mainCarouselIndex"
                  class="carousel-height view-preview-carousel"
                  show-arrows="hover"
                  hide-delimiters
                  hide-delimiter-background
                  :cycle="false"
              >
                <v-carousel-item v-for="(img, i) in product.image" :key="i" class="view-preview-carousel-item">
                  <v-img :src="img" class="view-preview-image" contain @click="openPreviewOverlay(i)"></v-img>
                </v-carousel-item>
              </v-carousel>
              <div v-if="product.image?.length > 1" class="thumbnail-gallery">
                <button
                    v-for="(img, i) in product.image"
                    :key="`edit-thumb-${i}`"
                    type="button"
                    class="thumbnail-btn"
                    :class="{ 'thumbnail-btn-active': mainCarouselIndex === i }"
                    :aria-label="`Show image ${i + 1}`"
                    @click="mainCarouselIndex = i"
                >
                  <v-img :src="img" class="thumbnail-image" cover></v-img>
                </button>
              </div>
              <img v-if="!product.image?.length" class="default-logo preview-empty-image" src="../../assets/images/lohnakas.webp" alt="Lohnakas logo"/>
            </v-sheet>
            <v-sheet class="rounded-lg order-summary" elevation="5">
              <div class="d-flex align-center flex-1-1 preview-order-inner">
                <div class="d-flex text-left flex-column w-100">
                  <h2> {{ getHeader(product) }} </h2>
                  <div class="product-category-text" v-if="getCategoryDisplayGroups(product).length">
                    <div class="product-category-group" v-for="(group, groupIndex) in getCategoryDisplayGroups(product)" :key="`edit-detail-group-${groupIndex}`">
                      <template v-for="(part, index) in group.pathParts" :key="`edit-detail-cat-${groupIndex}-${index}`">
                        <span class="product-category-level" :class="index === 0 ? 'product-category-main' : 'product-category-sub'">{{ part }}</span>
                        <span v-if="index < group.pathParts.length - 1" class="product-category-arrow">›</span>
                      </template>
                      <template v-for="(leaf, leafIndex) in group.leafLabels" :key="`edit-detail-leaf-${groupIndex}-${leafIndex}`">
                        <span class="product-category-level product-category-sub product-category-extra">{{ leaf }}</span>
                      </template>
                    </div>
                  </div>
                  <div class="tiptap tiptap-editor py-3" v-html=getBody(product)></div>
                  <div class="purchase-config">
                    <div class="config-card">
                      <div class="config-label">{{ $t('valige_kogus') }}</div>
                      <div class="quantity-picker">
                        <v-btn color="eraklient" icon="mdi-minus" size="30px"><v-icon size="20px"></v-icon></v-btn>
                        <div class="quantity-input-wrap">
                          <input class="quantity-input" v-model="quantity">
                        </div>
                        <v-btn color="eraklient" icon="mdi-plus" size="30px"><v-icon size="20px"></v-icon></v-btn>
                      </div>
                    </div>
                    <div v-if="product.has_spray_roll_on_option" class="config-card">
                      <div class="config-label">{{ $t('lohna_tuup') }}</div>
                      <div class="option-toggle">
                        <button type="button" :class="['option-pill', { 'option-pill--active': choice === 'Spray' }]" @click="choice = 'Spray'">Spray</button>
                        <button type="button" :class="['option-pill', { 'option-pill--active': choice === 'Roll-on' }]" @click="choice = 'Roll-on'">Roll-on</button>
                      </div>
                    </div>
                    <div v-if="product.bottle_sizes && product.bottle_sizes.length > 0" class="config-card">
                      <div class="config-label">{{ $t('vali_pudeli_suurus') }}</div>
                      <div class="option-toggle">
                        <button
                          v-for="bs in product.bottle_sizes"
                          :key="bs.size"
                          type="button"
                          :class="['option-pill', { 'option-pill--active': size === bs.size }]"
                          @click="size = bs.size"
                        >{{ bs.size }}</button>
                      </div>
                    </div>
                  </div>
                  <div class="purchase-footer">
                    <div class="purchase-price-block">
                      <div class="purchase-price-label">HIND</div>
                      <div class="purchase-price-row">
                        <template v-if="hasBottleSizePriceRange(product)">
                          <span class="purchase-price-value">{{ getPriceLabel(product, size) }}</span>
                        </template>
                        <template v-else>
                          <span class="purchase-price-value" :class="{ 'purchase-price-value--discount': product.new_price }">{{ product.new_price || product.price }}</span>
                          <span class="purchase-price-currency">EUR</span>
                        </template>
                      </div>
                    </div>
                  </div>
                  <v-btn class="product-cart-btn" color="eraklient" block>{{ $t('lisa_korvi') }}</v-btn>
                </div>
              </div>
            </v-sheet>
          </v-sheet>
        </v-window-item>
      </v-window>
      <v-overlay v-model="previewDialog" class="preview-image-overlay" :scrim="true" @click:outside="closePreviewOverlay">
        <div class="preview-overlay-content">
          <v-btn
              class="preview-overlay-close"
              icon="mdi-close"
              variant="text"
              color="white"
              @click="closePreviewOverlay"
          ></v-btn>
          <v-carousel
              v-if="product.image?.length"
              v-model="fullscreenIndex"
              class="preview-overlay-carousel"
              show-arrows="hover"
              hide-delimiters
              hide-delimiter-background
              :cycle="false"
          >
            <v-carousel-item
                v-for="(img, i) in product.image"
                :key="`preview-overlay-${i}`"
                class="preview-overlay-carousel-item"
            >
              <v-img
                  :src="img"
                  class="preview-overlay-image"
                  contain
              ></v-img>
            </v-carousel-item>
          </v-carousel>
          <div v-if="product.image?.length > 1" class="thumbnail-gallery overlay-thumbnail-gallery">
            <button
                v-for="(img, i) in product.image"
                :key="`edit-overlay-thumb-${i}`"
                type="button"
                class="thumbnail-btn overlay-thumbnail-btn"
                :class="{ 'thumbnail-btn-active': fullscreenIndex === i }"
                :aria-label="`Show fullscreen image ${i + 1}`"
                @click="fullscreenIndex = i"
            >
              <v-img :src="img" class="thumbnail-image" cover></v-img>
            </button>
          </div>
        </div>
      </v-overlay>
    </div>
  </v-sheet>
</template>

<script>
import PageHeader from "@/components/PageHeader";
import Tiptap from "@/components/Tiptap";
import Alert from "@/components/Alert";
import { getProductPriceLabel as resolveProductPriceLabel, hasBottleSizePriceRange as productHasBottleSizePriceRange } from "@/utils/productOptions.js";
export default {
  name: "EditProduct",
  components: {Alert, Tiptap, PageHeader},
  data() {
    return {
      valid: false,
      tab: null,
      lang_tab: null,
      quantity: 1,
      choice: "Spray",
      size: null,
      previewHovered: false,
      previewDialog: false,
      fullscreenIndex: 0,
      mainCarouselIndex: 0,
      selectedImage: null,
      uploadingImage: false,
      uploadError: "",
      loadingCloudinaryImages: false,
      cloudinaryError: "",
      cloudinaryImages: [],
      deletingPublicId: null,
      deleteImageDialog: false,
      pendingDeleteImage: null,
      categories: this.$store.state.categories,
      product: {
        id: null,
        category_ids: [],
        image: [],
        header: "",
        header_en: null,
        header_ru: null,
        description: "",
        description_en: null,
        description_ru: null,
        price: "",
        new_price: null,
        has_spray_roll_on_option: true,
        spray_roll_on_options: { spray: true, roll_on: true },
        bottle_sizes: [],
      },
      rules: [
        value => {
          if (value) return true
          return 'Ei saa olla tühi.'
        },
      ]
    }
  },
  computed: {},
  methods: {
    isSprayRollOnSelected() {
      const value = this.product?.has_spray_roll_on_option;

      if (typeof value === 'string') {
        return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
      }

      return value === true || value === 1;
    },
    getSecondaryPriceLabel() {
      return 'Soodushind';
    },
    formatPriceForDisplay(value) {
      if (value === null || value === undefined || value === '') return '';

      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return Number(numericValue.toFixed(2)).toString();
      }

      return String(value).trim();
    },
    isDefaultLogoImage(url) {
      const normalizedUrl = String(url || '').trim().toLowerCase();
      if (!normalizedUrl) return true;

      return normalizedUrl.includes('lohnakas.webp') || normalizedUrl.includes('lohnakas.png');
    },
    sanitizeProductImages(images) {
      const normalized = Array.isArray(images)
          ? images
          : images
              ? [images]
              : [];

      return [...new Set(
          normalized
              .map((url) => String(url || '').trim())
              .filter(Boolean)
              .filter((url) => !this.isDefaultLogoImage(url))
      )];
    },
    normalizeProduct(data) {
      const sprayRollOnValue = data?.has_spray_roll_on_option;
      const hasSprayRollOnOption = sprayRollOnValue === undefined || sprayRollOnValue === null
        ? true
        : typeof sprayRollOnValue === 'string'
          ? !['0', 'false', 'no'].includes(sprayRollOnValue.trim().toLowerCase())
          : Boolean(sprayRollOnValue);

      const bottleSizesRaw = data?.bottle_sizes;
      let bottleSizes = [];
      if (bottleSizesRaw) {
        bottleSizes = Array.isArray(bottleSizesRaw)
          ? bottleSizesRaw
          : (() => { try { return JSON.parse(bottleSizesRaw); } catch { return []; } })();
      }
      // Auto-migrate old products: if no bottle_sizes but has_bottle_size_option was true, pre-populate
      if (bottleSizes.length === 0) {
        const bottleSizeValue = data?.has_bottle_size_option;
        const hasBottleSizeOption = bottleSizeValue === undefined || bottleSizeValue === null
          ? false
          : typeof bottleSizeValue === 'string'
            ? !['0', 'false', 'no'].includes(bottleSizeValue.trim().toLowerCase())
            : Boolean(bottleSizeValue);
        if (hasBottleSizeOption) {
          bottleSizes = [
            { size: '100ml', price: data.price ?? '' },
            { size: '250ml', price: data.new_price ?? '' },
          ];
        }
      }

      const incomingIds = Array.isArray(data?.category_ids)
          ? data.category_ids
          : [];

      return {
        ...data,
        category_ids: incomingIds,
        has_spray_roll_on_option: hasSprayRollOnOption,
        spray_roll_on_options: (() => {
          const raw = data?.spray_roll_on_options;
          if (!raw) return { spray: true, roll_on: true };
          if (typeof raw === 'object' && !Array.isArray(raw)) return { spray: raw.spray !== false, roll_on: raw.roll_on !== false };
          try { const p = JSON.parse(raw); return { spray: p.spray !== false, roll_on: p.roll_on !== false }; } catch { return { spray: true, roll_on: true }; }
        })(),
        bottle_sizes: bottleSizes,
        image: this.sanitizeProductImages(
            Array.isArray(data.image)
                ? data.image
                : data.image
                    ? [data.image]
                    : []
        ),
      };
    },
    fetchAProduct(id) {
      fetch(`${process.env.VUE_APP_SERVER_URL}/api/products/${id}`)
          .then((response) => response.json())
          .then((data) => (this.product = this.normalizeProduct(data)))
          .catch((err) => console.log(err.message));
    },
    async fetchCloudinaryImages() {
      this.loadingCloudinaryImages = true;
      this.cloudinaryError = "";

      try {
        const response = await fetch(`${process.env.VUE_APP_SERVER_URL}/api/uploads/images?t=${Date.now()}`, {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Failed to load Cloudinary images");
        }

        this.cloudinaryImages = data;
      } catch (error) {
        this.cloudinaryError = "Cloudinary piltide laadimine ebaonnestus";
        console.log(error);
      } finally {
        this.loadingCloudinaryImages = false;
      }
    },
    isSelectedImage(url) {
      return this.product.image.includes(url);
    },
    setProductImages(images) {
      const normalizedImages = Array.isArray(images)
          ? images.map((url) => String(url || '').trim()).filter(Boolean)
          : [];

      this.product.image = normalizedImages;
      this.mainCarouselIndex = 0;
      this.fullscreenIndex = 0;
    },
    removeProductImage(index) {
      const nextImages = [...this.product.image];
      nextImages.splice(index, 1);
      this.setProductImages(nextImages);
    },
    moveProductImage(index, direction) {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= this.product.image.length) return;

      const nextImages = [...this.product.image];
      [nextImages[index], nextImages[nextIndex]] = [nextImages[nextIndex], nextImages[index]];
      this.setProductImages(nextImages);
    },
    setProductCoverImage(index) {
      if (index <= 0 || index >= this.product.image.length) return;

      const nextImages = [...this.product.image];
      const [coverImage] = nextImages.splice(index, 1);
      nextImages.unshift(coverImage);
      this.setProductImages(nextImages);
    },
    selectCloudinaryImage(image) {
      if (this.isSelectedImage(image.url)) {
        this.setProductImages(this.product.image.filter((url) => url !== image.url));
        return;
      }

      this.setProductImages([...this.product.image, image.url]);
    },
    async uploadImage(fileInput) {
      const files = Array.isArray(fileInput) ? fileInput : (fileInput ? [fileInput] : []);
      if (!files.length) return;

      this.uploadError = "";
      this.uploadingImage = true;

      try {
        const uploadedUrls = [];

        for (const file of files) {
          const formData = new FormData();
          formData.append("image", file);

          const response = await fetch(`${process.env.VUE_APP_SERVER_URL}/api/uploads/image`, {
            method: "POST",
            body: formData,
            credentials: "include",
          });

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || "Image upload failed");
          }

          uploadedUrls.push(data.url);
        }

        this.setProductImages([...new Set([...this.product.image, ...uploadedUrls])]);
        this.selectedImage = null;
      } catch (error) {
        this.uploadError = "Pildi üleslaadimine ebaõnnestus";
        console.log(error);
      } finally {
        this.uploadingImage = false;
      }
    },
    openDeleteCloudinaryDialog(image) {
      this.pendingDeleteImage = image;
      this.deleteImageDialog = true;
    },
    closeDeleteCloudinaryDialog() {
      this.deleteImageDialog = false;
      this.pendingDeleteImage = null;
    },
    async confirmDeleteCloudinaryImage() {
      const image = this.pendingDeleteImage;
      if (!image?.public_id) return;

      this.cloudinaryError = "";
      this.deletingPublicId = image.public_id;

      try {
        const response = await fetch(`${process.env.VUE_APP_SERVER_URL}/api/uploads/image`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ public_id: image.public_id }),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Image delete failed");
        }

        this.cloudinaryImages = this.cloudinaryImages.filter((item) => item.public_id !== image.public_id);
        this.setProductImages(this.product.image.filter((url) => url !== image.url));
      } catch (error) {
        this.cloudinaryError = "Pildi kustutamine ebaonnestus";
        console.log(error);
      } finally {
        this.deletingPublicId = null;
        this.closeDeleteCloudinaryDialog();
      }
    },
    async updateProduct() {
      const { valid } = await this.$refs.form.validate()

      if (valid) {
        const payload = {
          ...this.product,
          category_ids: (this.product.category_ids || []).filter(Boolean),
          image: this.sanitizeProductImages(this.product.image),
          has_spray_roll_on_option: this.product.has_spray_roll_on_option,
          spray_roll_on_options: this.product.has_spray_roll_on_option ? this.product.spray_roll_on_options : null,
          bottle_sizes: (this.product.bottle_sizes || []).filter(bs => bs.size),
        };

        this.product.image = payload.image;

        fetch(`${process.env.VUE_APP_SERVER_URL}/api/products/${this.product.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        })
            .then((response) => {
              console.log(response.data);
              location.assign("/tooted");
            })
            .catch((e) => {
              console.log(e);
            });
      }
    },
    deleteProduct() {
      fetch(`${process.env.VUE_APP_SERVER_URL}/api/products/${this.product.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      })
          .then((response) => {
            console.log(response.data);
            location.assign("/tooted");
          })
          .catch((e) => {
            console.log(e);
          });
    },
    changeLocale(value) {
      this.$i18n.locale = value;
    },
    getHeader(content) {
      return (this.$i18n.locale === 'en' && content.header_en !== null) ? content.header_en : (this.$i18n.locale === 'ru' && content.header_ru !== null) ? content.header_ru : content.header;
    },
    getBody(content) {
      return (this.$i18n.locale === 'en' && content.description_en !== null) ? content.description_en : (this.$i18n.locale === 'ru' && content.description_ru !== null) ? content.description_ru : content.description;
    },
    hasBottleSizePriceRange(content) {
      return productHasBottleSizePriceRange(content);
    },
    getPriceLabel(content, size) {
      return resolveProductPriceLabel(content, size);
    },
    getCategoryLabelById(categoryId) {
      const category = this.categories.find((cat) => cat.id === categoryId);
      if (!category) return '';

      const categoryName = (this.$i18n.locale === 'en' && category.name_en !== null) ? category.name_en : (this.$i18n.locale === 'ru' && category.name_ru !== null) ? category.name_ru : category.name;
      return String(categoryName || '')
          .split(' - ')
          .map((part) => part.trim())
          .filter(Boolean);
    },
    getCategoryDisplayGroups(content) {
      const ids = Array.isArray(content?.category_ids)
          ? content.category_ids
          : [];

      const categoryPaths = ids
          .map((categoryId) => this.getCategoryLabelById(categoryId))
          .filter((parts) => parts.length);

      const uniqueCategoryPaths = [];
      categoryPaths.forEach((pathParts) => {
        const pathKey = pathParts.join(' - ');
        if (!uniqueCategoryPaths.some((entry) => entry.pathKey === pathKey)) {
          uniqueCategoryPaths.push({ pathKey, pathParts });
        }
      });

      const groupedPaths = [];

      uniqueCategoryPaths.forEach(({ pathParts }) => {
        const groupKey = pathParts.slice(0, -1).join(' - ') || pathParts[0];
        let group = groupedPaths.find((entry) => entry.groupKey === groupKey);
        if (!group) {
          group = {
            groupKey,
            paths: [],
          };
          groupedPaths.push(group);
        }

        group.paths.push(pathParts);
      });

      return groupedPaths.map((group) => {
        const commonPrefix = group.paths.reduce((prefix, pathParts) => {
          if (!prefix.length) return pathParts.slice();

          let index = 0;
          while (index < prefix.length && index < pathParts.length && prefix[index] === pathParts[index]) {
            index += 1;
          }

          return prefix.slice(0, index);
        }, []);

        const pathParts = commonPrefix.length ? commonPrefix : group.paths[0];
        const leafLabels = [];

        group.paths.forEach((pathPartsItem) => {
          const suffixParts = pathPartsItem.slice(pathParts.length).filter(Boolean);
          const leaf = suffixParts.join(' › ');
          if (leaf && !leafLabels.includes(leaf)) {
            leafLabels.push(leaf);
          }
        });

        return {
          pathParts,
          leafLabels,
        };
      }).filter((group) => group.pathParts.length || group.leafLabels.length);
    },
    addCategory() {
      if (!Array.isArray(this.product.category_ids)) this.product.category_ids = [];
      this.product.category_ids.push('');
    },
    removeCategory(idx) {
      if (!Array.isArray(this.product.category_ids)) return;
      this.product.category_ids.splice(idx, 1);
    },
    addBottleSize() {
      if (!Array.isArray(this.product.bottle_sizes)) this.product.bottle_sizes = [];
      this.product.bottle_sizes.push({ size: '', price: '', disabled: false });
    },
    removeBottleSize(idx) {
      if (!Array.isArray(this.product.bottle_sizes)) return;
      this.product.bottle_sizes.splice(idx, 1);
    },
    validateCategoryUnique(value, idx) {
      if (!value) return true;
      const num = Number(value);
      if (!Number.isFinite(num)) return true;
      const others = (this.product.category_ids || []).filter((_, i) => i !== idx).map((v) => Number(v));
      return !others.includes(num);
    },
    getLinkCat(content) {
      return this.$store.getters.getLinkCat(content);
    },
    setPreviewHover(isHovering) {
      this.previewHovered = isHovering;
    },
    openPreviewOverlay(index) {
      this.fullscreenIndex = Number(index) || 0;
      this.previewDialog = true;
    },
    closePreviewOverlay() {
      this.previewDialog = false;
    }
  },
  created() {
    this.$store.dispatch('fetchProducts');
    this.$store.dispatch('fetchCategories');
    this.fetchCloudinaryImages();
  },
  mounted() {
    this.fetchAProduct(this.$route.params.id);
  }
}
</script>
<style scoped>
.v-card-text {
  padding: 0;
}

.preview-tabs-window :deep(.v-window__container) {
  height: auto !important;
}

.preview-tabs-window :deep(.v-window-item) {
  overflow: visible !important;
}

.preview-detail-tab {
  padding: 20px;
}

.content-body {
  overflow: visible;
  align-items: flex-start;
  gap: 20px;
}

.view-preview-media {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: auto;
  flex: 1 1 60%;
  min-width: 320px;
  max-width: 760px;
  overflow: visible;
  gap: 12px;
}

.preview-tabs-window :deep(.prod-item.product-img) {
  width: 100% !important;
  max-width: 500px;
  min-width: 300px;
  margin: 0 auto;
}

.preview-empty-image {
  padding: 20px;
}

.preview-order-inner {
  padding: 20px;
}

.error-text {
  color: #b00020;
  margin-top: -8px;
  margin-bottom: 12px;
}

.preview-card-image {
  position: relative;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-card-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-card-title {
  width: 100%;
  padding-left: 8px;
  padding-right: 8px;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  line-height: 1.25;
  text-align: center;
}

.preview-card-title .head {
  display: block;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.product-category-text {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  line-height: 1.2;
}

.product-category-group {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.product-category-level {
  display: inline-flex;
  align-items: center;
}

.purchase-price-block {
  display: grid;
  justify-items: start;
  gap: 4px;
}

.purchase-price-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.purchase-price-row {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
}

.purchase-price-value {
  font-size: 2.1rem;
  font-weight: 800;
  line-height: 1;
  color: #111827;
}

.purchase-price-value--discount {
  color: #e53935;
}

.purchase-price-currency {
  font-size: 1.55rem;
  font-weight: 700;
  line-height: 1;
  color: #111827;
}

.purchase-config {
  display: grid;
  gap: 12px;
  margin-top: 8px;
  margin-bottom: 18px;
}

.purchase-footer {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
}

.config-card {
  display: grid;
  gap: 8px;
}

.config-label {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.quantity-picker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.quantity-input-wrap {
  width: 48px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
}

.quantity-input {
  width: 100%;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  outline: none;
}

.option-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-pill {
  min-width: 92px;
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 999px;
  background: #ffffff;
  color: #1f2d3d;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
}

.option-pill--active {
  background: linear-gradient(135deg, #3b71f7 0%, #5b8cff 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(59, 113, 247, 0.2);
}

.product-cart-btn {
  margin-top: 8px;
  border-radius: 999px !important;
  background: linear-gradient(135deg, #3b71f7 0%, #5b8cff 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 10px 20px rgba(59, 113, 247, 0.2) !important;
}

.product-category-main {
  font-size: 14px;
  font-weight: 700;
  color: #1f2d3d;
  background: #eef4ff;
  border: 1px solid #dbe8ff;
  border-radius: 999px;
  padding: 3px 9px;
}

.product-category-sub {
  font-size: 13px;
  font-weight: 400;
  color: #5d6b7a;
  background: #f7f9fc;
  border: 1px solid #e7edf6;
  border-radius: 999px;
  padding: 3px 9px;
}

.product-category-extra {
  margin-left: 4px;
}

.product-category-arrow {
  color: #93a1b2;
  font-size: 12px;
  font-weight: 700;
}

.preview-layered-images {
  position: relative;
  width: 100%;
  height: 100%;
}

.preview-carousel-image {
  width: 100%;
  height: 100%;
}

.base-image,
.hover-image {
  position: absolute;
  inset: 0;
  transition: opacity 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
  will-change: opacity;
}

.base-image {
  opacity: 1;
}

.base-image.is-inactive {
  opacity: 0;
}

.hover-image {
  opacity: 0;
}

.hover-image.is-active {
  opacity: 1;
}

.preview-carousel-image :deep(.v-img__img) {
  object-fit: contain !important;
  object-position: center center !important;
}

.view-preview-carousel,
.view-preview-carousel :deep(.v-window),
.view-preview-carousel :deep(.v-window__container),
.view-preview-carousel :deep(.v-window-item),
.view-preview-carousel-item {
  width: 100%;
  height: 100% !important;
}

.view-preview-carousel {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.view-preview-image {
  width: 100%;
  height: 100%;
  cursor: zoom-in;
}

.view-preview-image :deep(.v-img__img) {
  object-fit: contain !important;
  object-position: center center !important;
  cursor: zoom-in;
}

.thumbnail-gallery {
  display: flex;
  gap: 10px;
  width: 100%;
  margin-top: 0;
  justify-content: center;
  flex-wrap: wrap;
}

.thumbnail-btn {
  width: 64px;
  height: 64px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease;
}

.thumbnail-btn:hover {
  border-color: #3b71f7;
  transform: translateY(-1px);
}

.thumbnail-btn-active {
  border-color: #3b71f7;
  box-shadow: 0 0 0 2px rgba(59, 113, 247, 0.15);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
}

.preview-image-overlay {
  z-index: 3000;
}

.preview-image-overlay :deep(.v-overlay__content) {
  width: 100vw;
  height: 100vh;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-overlay-content {
  position: relative;
  width: min(95vw, 1400px);
  height: min(92vh, 1000px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}

.preview-overlay-close {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 3100;
  background: rgba(0, 0, 0, 0.45);
}

.preview-overlay-carousel,
.preview-overlay-carousel :deep(.v-window),
.preview-overlay-carousel :deep(.v-window__container),
.preview-overlay-carousel :deep(.v-window-item),
.preview-overlay-carousel-item {
  width: 100%;
  height: 100% !important;
}

.preview-overlay-carousel {
  border-radius: 8px;
  overflow: hidden;
  flex: 1 1 auto;
}

.overlay-thumbnail-gallery {
  width: 100%;
  justify-content: center;
}

.overlay-thumbnail-btn {
  background: rgba(255, 255, 255, 0.95);
}

@media (max-width: 820px) {
  .post-body {
    padding: 0 20px !important;
  }
  .elevation-5 {
    box-shadow: none !important;
  }

  .content-body {
    gap: 0;
  }

  .preview-detail-tab {
    padding: 0 !important;
  }

  .view-preview-media {
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }

  .preview-tabs-window :deep(.prod-item.product-img) {
    min-width: 0;
    max-width: 100%;
    width: 100% !important;
  }

  .preview-empty-image {
    padding: 0;
    padding-bottom: 20px;
  }

  .preview-order-inner {
    padding: 16px;
    padding-bottom: 20px;
  }
}

.preview-overlay-image {
  width: 100%;
  height: 100%;
}

.preview-overlay-image :deep(.v-img__img) {
  object-fit: contain !important;
  object-position: center center !important;
}

.selected-images-hint {
  margin: 4px 0 0;
  color: #5d6b7a;
  font-size: 13px;
}

.product-image-manager {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.product-image-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
}

.product-image-item-cover {
  border-color: #3b71f7;
  box-shadow: 0 0 0 2px rgba(59, 113, 247, 0.08);
}

.product-image-thumb-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 10px;
  background: #f7f9fc;
}

.product-image-thumb {
  width: 100%;
  height: 100%;
}

.product-image-thumb :deep(.v-img__img) {
  object-fit: cover !important;
}

.product-image-badge {
  position: absolute;
  top: 8px;
  left: 8px;
}

.product-image-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.cloudinary-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.cloudinary-item {
  position: relative;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  padding: 0;
  background: transparent;
}

.cloudinary-item.selected {
  border-color: #4caf50;
}

.cloudinary-select {
  display: block;
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.cloudinary-item img {
  display: block;
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.cloudinary-delete {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 1;
}

</style>